import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@getro/rombo';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { userSelector } from 'redux/selectors';
import { useRequestBatchIntroMutation, useRequestIntroMutation } from 'services/intros';
import { trackEvent } from 'helpers/analytics';
import { useLocalStorage } from 'hooks/useLocalStorage';
import moment from 'moment';
import { createPortal } from 'react-dom';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import { Check } from 'lucide-react';
import RequestIntroModal from './requestIntroModal';

export const RequestIntro = ({
  selectedContacts,
  list,
  buttonProps = {},
  isSingleContact = true,
  clearSelectedContacts,
  slug,
  children,
}) => {
  const [showIntroModal, setShowIntroModal] = useState(false);
  const user = useSelector(userSelector);
  const [requestIntro, { isLoading }] = useRequestIntroMutation();
  const [requestBatchIntro, { isLoading: isLoadingBatch }] = useRequestBatchIntroMutation();
  const [requestedContacts, setRequestedContacts] = useLocalStorage(`requested_intros_${slug}`, [], {
    reactive: true,
    updateState: true,
  });

  const isBatchIntro = useMemo(() => selectedContacts.length > 1, [selectedContacts]);

  const handleRequestIntro = useCallback(
    async (values) => {
      const response = !isBatchIntro ? await requestIntro(values) : await requestBatchIntro(values);

      if (!response.error) {
        const contacts = values.contacts || selectedContacts;
        const newRequestedContacts = [
          ...requestedContacts,
          ...contacts.map((contact) => ({
            id: contact.id,
            createdAt: new Date(),
          })),
        ];

        setRequestedContacts(newRequestedContacts);
        clearSelectedContacts();

        (values.contacts || selectedContacts).forEach((value) => {
          trackEvent('intros:intro_requested', {
            sharedListId: list.id,
            contactId: value.id,
            notes: values.reason,
            collection_id: list.collection.id,
            in_bulk: isBatchIntro,
            bulk_hash: isBatchIntro ? response.data?.id : null,
            page: 'shared_list_view',
          });
        });
      }

      return response;
    },
    [
      isBatchIntro,
      requestIntro,
      requestBatchIntro,
      selectedContacts,
      requestedContacts,
      setRequestedContacts,
      clearSelectedContacts,
      list.id,
      list.collection.id,
    ],
  );

  const { hasRequestedIntro, formattedDate } = useMemo(() => {
    const contact = requestedContacts.find((requestedContact) => requestedContact.id === selectedContacts[0]?.id);
    let dateFormatted = null;
    if (contact) {
      if (moment(contact.createdAt).isSame(moment(), 'day')) {
        dateFormatted = 'today';
      } else {
        dateFormatted = moment(contact.createdAt).fromNow();
      }
    }
    return {
      hasRequestedIntro: !!contact,
      formattedDate: dateFormatted,
    };
  }, [requestedContacts, selectedContacts]);

  return (
    <>
      <DynamicTooltip
        position="top-start"
        content={`Intro requested ${formattedDate}`}
        useTooltip={hasRequestedIntro}
        trigger={
          <Button
            icon={hasRequestedIntro ? Check : null}
            iconProps={{
              size: '16px',
            }}
            iconGap="8px"
            variant={hasRequestedIntro ? 'tertiary' : 'accent'}
            width="100%"
            size="small"
            bg={hasRequestedIntro ? 'transparent' : 'purple.200'}
            justifyContent="center"
            fontWeight={hasRequestedIntro ? '400' : '500'}
            onClick={() => {
              if (hasRequestedIntro) {
                return;
              }

              setShowIntroModal(true);
              trackEvent('intros:request_intro_click', {
                collection_id: list.collection.id,
                list_id: list.id,
                in_bulk: isBatchIntro,
                total_selected_contacts: selectedContacts.length,
                page: 'shared_list_view',
              });
            }}
            {...buttonProps}
          >
            {children || (hasRequestedIntro ? `Requested` : `Request intro${isSingleContact ? '' : 's'}`)}
          </Button>
        }
      />

      {showIntroModal &&
        createPortal(
          <RequestIntroModal
            user={user.id ? user : null}
            list={list}
            selectedContacts={selectedContacts}
            isOpen={showIntroModal}
            onClose={() => setShowIntroModal(false)}
            onRequestIntro={handleRequestIntro}
            isLoading={isLoading || isLoadingBatch}
            clearSelectedContacts={clearSelectedContacts}
            isSingleContact={!isBatchIntro}
          />,
          document.body,
        )}
    </>
  );
};

RequestIntro.propTypes = {
  selectedContacts: PropTypes.array.isRequired,
  list: PropTypes.object.isRequired,
  buttonProps: PropTypes.object,
  isSingleContact: PropTypes.bool,
  clearSelectedContacts: PropTypes.func,
  slug: PropTypes.string.isRequired,
  children: PropTypes.node,
};

RequestIntro.defaultProps = {
  buttonProps: {},
  isSingleContact: true,
  clearSelectedContacts: () => {},
  children: null,
};
